export const LIGHTRAG_BASE_URL = "https://affect-reduction-cork-cancer.trycloudflare.com";

const HEADERS: HeadersInit = {
  "Content-Type": "application/json",
  "ngrok-skip-browser-warning": "true",
};

async function lightragFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const url = `${LIGHTRAG_BASE_URL}${path}`;
  console.log(`[LightRAG] Fetching: ${url}`);
  const response = await fetch(url, {
    ...init,
    headers: { ...HEADERS, ...(init?.headers ?? {}) },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    console.error(`[LightRAG] HTTP ${response.status} for ${url}`, body);
    throw new Error(`LightRAG API error: HTTP ${response.status} – ${body}`);
  }

  const data = await response.json() as T;
  console.log(`[LightRAG] Response from ${path}:`, data);
  return data;
}

/* ── Label endpoints ── */

export interface GraphLabel {
  name: string;
  count?: number;
}

export async function fetchGraphLabels(): Promise<string[]> {
  const data = await lightragFetch<any>("/graph/label/list");
  // API returns either string[] or { labels: string[] }
  if (Array.isArray(data)) return data;
  if (data?.labels && Array.isArray(data.labels)) return data.labels;
  return [];
}

export interface GraphEntity {
  id: string;
  entity_name?: string;
  name?: string;
  description?: string;
  entity_type?: string;
  source_id?: string;
  [key: string]: unknown;
}

export async function fetchGraphEntitiesByLabel(label: string): Promise<GraphEntity[]> {
  const data = await lightragFetch<any>(`/graph/label/entity?label=${encodeURIComponent(label)}`);
  if (Array.isArray(data)) return data;
  if (data?.entities && Array.isArray(data.entities)) return data.entities;
  return [];
}

export async function fetchGraphChunksByLabel(label: string): Promise<any[]> {
  const data = await lightragFetch<any>(`/graph/label/chunk?label=${encodeURIComponent(label)}`);
  if (Array.isArray(data)) return data;
  if (data?.chunks && Array.isArray(data.chunks)) return data.chunks;
  return [];
}

/* ── Entity detail ── */

export async function fetchEntityDetail(entityId: string): Promise<GraphEntity | null> {
  try {
    const data = await lightragFetch<any>(`/graph/entity/exists?entity_name=${encodeURIComponent(entityId)}`);
    return data ?? null;
  } catch (err) {
    console.error("[LightRAG] fetchEntityDetail failed:", err);
    return null;
  }
}

/* ── Query with graph context ── */

export interface LightRAGQueryResult {
  response: string;
  entities?: string[];
  [key: string]: unknown;
}

export interface GraphNode {
  id: string;
  labels?: string[];
  entity_name?: string;
  entity_type?: string;
  description?: string;
  properties?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface GraphEdge {
  source: string;
  target: string;
  properties?: Record<string, unknown>;
  [key: string]: unknown;
}

export async function fetchGraphData(): Promise<{ nodes: GraphNode[]; edges: GraphEdge[] }> {
  try {
    const data = await lightragFetch<any>("/graphs?label=*&max_depth=3&max_nodes=500");
    console.log("[LightRAG] Graph data received:", { nodeCount: data?.nodes?.length, edgeCount: data?.edges?.length, keys: Object.keys(data || {}) });
    const nodes: GraphNode[] = Array.isArray(data?.nodes) ? data.nodes : [];
    const edges: GraphEdge[] = Array.isArray(data?.edges) ? data.edges : [];
    return { nodes, edges };
  } catch (err) {
    console.error("[LightRAG] fetchGraphData failed:", err);
    return { nodes: [], edges: [] };
  }
}

export async function queryLightRAG(query: string, mode: string = "hybrid"): Promise<LightRAGQueryResult> {
  const data = await lightragFetch<any>("/query", {
    method: "POST",
    body: JSON.stringify({ query, mode }),
  });
  return {
    response: typeof data?.response === "string" ? data.response : "",
    entities: Array.isArray(data?.entities) ? data.entities : [],
    ...data,
  };
}
